const _ = require("lodash");
const browser = require("detect-browser").detect();
const moment = require("moment");

const Bundles = require("../Bundles");
const AT = require("./ActionTypes");
const {FLAGS} = require("../util/GlobalFlags");
const ErrorUtil = require("../../../shared/ErrorUtil");
const DataDogRUM = require("../util/DataDogRUM");
const PageShared = require("../../../shared/PageShared");
const Sounds = require("../../core/util/Sounds");
const OpsLab = require("../../lab/vault/OpsLab");
const OpsLibrary = require("../../library/vault/OpsLibrary");
const Analytics = require("../util/Analytics");
const MultiSlots = require("./MultiSlots");
const BrowserUtil = require("../util/BrowserUtil");
const Mixpanel = require("../util/Mixpanel");
const {shouldRemoveCampusView} = require("../util/NavigationUtil");
const {
	getOnePlacementUrl,
	getMixpanelDataFromUser,
} = require("../home/util/PageOnboardingUtil");
const {setIn} = require("../../../shared/ImmHelpers");
const {resetVaultState} = require("./StateReset");
const {
	atCart,
	atAllLocks,
	atBlocks,
	atChapter,
	atChapterPath,
	atCMSInfoPage,
	atCurrBlock,
	atCurrProblem,
	atCurrResult,
	atDemo,
	atDemoData,
	atLabBlocks,
	atLabType,
	atLeaderboards,
	atLeaderboardsRankings,
	atLibraryChaptersByGrade,
	atMarketing,
	atOfficeStudentData,
	atPage,
	atReadAloud,
	atReportAssignedBlocks,
	atTempUser,
	atTheaterSet,
	atTimeTracking,
	atTutorial,
	atUser,
	atUserOrClassLocks,
	atLibraryChapter,
} = require("./StateCursors");

const DerivedBackend = require("./DerivedBackend");
const APIFormat = require("./util/APIFormat");

const LoadState = require("./util/LoadState");
const Slots = require("./Slots");

const OpsPage = require("./OpsPage");
const OpsUser = require("./OpsUser");
const OpsBlock = require("./OpsBlock");
const OpsPageData = require("./OpsPageData");
const TimeTracking = require("./TimeTracking");
const OpsMarketing = require("./OpsMarketing");
const OpsTheater = require("./OpsTheater");
const OpsDemo = require("./OpsDemo");
const OpsLockOverrides = require("./OpsLockOverrides");
const OpsTutorial = require("./OpsTutorial");
const OpsChapterPath = require("./OpsChapterPath");

const ChapterSection = require("../../../shared/ChapterSection");
const Constants = require("../../../shared/Constants");
const GradeChapter = require("../../../shared/GradeChapter");
const ResultManager = require("../../../shared/ResultManager");
const TrainerCore = require("../../../shared/TrainerCore");
const URLMake = require("../../../shared/URLMake");
const {getScreenWarning} = require("../util/ContainerSize");

// See StateReset for the basic state shape.

//
// The main action handlers
//

const actionHandlers = {
	[AT.INITIAL](vault, state, action) {
		const isServerSide = action.isServerSide;
		if (isServerSide && action.state) {
			// The URL is authoritative on hydration. Preloaded state may carry a
			// different page (e.g. home.main from sequence data) while SSR HTML and
			// the address bar still reflect the current route (e.g. / → office.main).
			state = setIn(action.state, "page", action.pageState);
			state = initializeVault(vault, state);
			// Fall back to a client user load when preloaded state is incomplete.
			// Without this, HomeHeader can stay on its skeleton forever because
			// authenticated never flips true and startUserLoad is skipped.
			//
			// Use loadRestart (not loadStart): SSR runs with a mock API that never
			// finishes, so preloaded user often arrives as load: "pending". loadStart
			// no-ops in that state and leaves MultiLoadGuard (e.g. redeemsubscription)
			// waiting forever.
			if (!OpsUser.isLoggedIn(atUser(state).get())) {
				state = restartUserLoad(vault, state);
			}
			return state;
		}
		return initializeVaultAndSession(vault, state, {
			isFirstAction: true,
			pageState: action.pageState,
			shouldReset: true,
		});
	},

	[AT.FINAL](vault, state, action) {
		// Other bundles may add post update hooks specifically for this action.
		vault.emitEvent("readAloudCancel");
		vault.throttler.flushAll();
		return state;
	},

	[AT.SET_CONNECTED](vault, state, action) {
		return setIn(state, "connected", action.connected);
	},

	[AT.STORAGE_UPDATED](vault, state, action) {
		// This action can be dispatched from the core bundle, but nothing in
		// core needs to handle it. Use a post-update hook to have other bundles
		// add functionality to it.
	},

	// TODO: how to integrate with CHECK_BROWSER
	[AT.SET_BAD_BROWSER](vault, state, action) {
		vault.emitEvent("showBrowserWarning");
	},

	[AT.CHECK_BROWSER](vault, state, action) {
		const skipBrowserMessages =
			vault.storage.getItem("suppressBrowserWarnings") ||
			FLAGS.skipBrowserMessages;
		if (!skipBrowserMessages && browser) {
			// If the user is on a non-supported browser, warn them it is not
			// supported [T1656].
			const recommendedBrowsers = Constants.RECOMMENDED_BROWSERS;
			const allowedBrowsers = Constants.ALLOWED_BROWSERS;
			// TODO: we've only tested on iPad, will this check for other tablet
			// devices?
			const allowedMobileBrowsers = Constants.ALLOWED_MOBILE_BROWSERS;

			const versionRequirements = {
				firefox: 46,
				// For Chromium MS edge, browser name appeared as "chrome" during testing.
				// It can change its own user agent string conditionally (see below)
				// https://winaero.com/blog/microsoft-edge-chromium-dynamically-changes-its-user-agent/
				chrome: 40,
				safari: 11,
				// Need to update this number to catch the case where a user is on old edge since
				// browser update message will need to tell them a version number for chromium edge
				edge: 80,
				ios: 10,
				crios: 40,
				fxios: 0,
			};
			const versionNumber = parseInt(/^(\d+)/.exec(browser.version)[0]);
			const outdated =
				_.isFinite(versionNumber) &&
				versionNumber < versionRequirements[browser.name];

			// Show a message if they are on a browser that isn't recommended. Lock
			// it if it isn't a browser that's allowed or one that is below the
			// version cutoff.
			if (
				!_.includes(
					recommendedBrowsers.concat(allowedMobileBrowsers),
					browser.name
				)
			) {
				const supported = _.includes(allowedBrowsers, browser.name);
				let message = "browserNotRecommended";
				if (!supported) {
					message = "browserNotSupported";
				} else if (outdated) {
					message = "browserNotRecommendedAndOutdated";
				}
				const cannotClose = !supported || outdated;
				vault.emitEvent("showMessage", message, {
					browser: _.capitalize(browser.name),
					version: browser.version,
					requiredVersion: versionRequirements[browser.name],
					cannotClose,
					doNotShowAgainOption: !cannotClose,
					onCloseDispatch: {
						type: AT.SUPPRESS_BROWSER_WARNINGS,
					},
				});
			} else if (outdated) {
				// Then check for recommended browsers if they have at least the
				// required version. If not, show a message that cannot be closed.
				vault.emitEvent("showMessage", "browserOutdated", {
					browser: _.capitalize(browser.name),
					version: browser.version,
					requiredVersion: versionRequirements[browser.name],
					cannotClose: true,
				});
			} else {
				//Finally, check for the specific Mac + Chrome combo with filter issues
				const userAgent = global.navigator && global.navigator.userAgent;
				if (
					browser.name === "chrome" &&
					/Mac OS X 10[._]1[01]/.test(userAgent)
				) {
					vault.emitEvent("showMessage", "browserMacChromeVersionIssue", {
						osVersion: /Mac OS X 10[._]10/.test(userAgent) ? "10.10" : "10.11",
						cannotClose: true,
					});
				}
			}
		}
		return state;
	},

	[AT.SUPPRESS_BROWSER_WARNINGS](vault, state, action) {
		vault.storage.setItem("suppressBrowserWarnings", true);
	},

	[AT.DEVICE_ROTATE](vault, state, action) {
		// Forcing the postupdate hook to check screen size
		if (state.screenWarning === "ignore") {
			return state;
		}
		return setIn(state, "screenWarning", null);
	},

	[AT.IGNORE_SCREEN_WARNING](vault, state, action) {
		// Set a screen warning always to be ignored when playing on a phone
		return setIn(state, "screenWarning", "ignore");
	},

	[AT.SET_NO_COOKIES](vault, state, action) {
		vault.emitEvent("showMessage", "cookieWarning", {refreshOnClose: true});
	},

	// Server only at the moment
	[AT.SET_TOKEN](vault, state, action) {
		return atUser(state).extend({
			token: action.token || null,
			tokenExpiration: action.tokenExpiration || null,
		});
	},

	/* TIMING */
	[AT.START_IDLE_OR_BLUR](vault, state, action) {
		// When the user blurs the window (by switching to a new tab, for instance)
		// we mute the music.
		if (!action.isIdle) {
			vault.sounds.setMusicMuted(true);
		}
		return setIn(state, "idle", true);
	},
	[AT.END_IDLE_OR_BLUR](vault, state, action) {
		updateMusicEnabled(vault, state);
		return setIn(state, "idle", false);
	},
	[AT.NOTIFY_SLEEP](vault, state, action) {
		// Noop, but runs hooks that update timers. Some hooks give this action
		// special treatment.
		return state;
	},
	[AT.UPDATE_TIMERS](vault, state, action) {
		// Noop, but runs hooks that update timers.
		return state;
	},

	[AT.PASSWORD_RESET_REQUEST](vault, state, action) {
		let possibleDomain = "";
		const page = atPage(state).get();
		const pageOpts = _.get(page, ["domain"]);
		if (pageOpts) {
			const urlParts = pageOpts.split("/");
			possibleDomain = _.first(urlParts).toLowerCase();
		}
		vault.api.passwordReset(
			action.username,
			action.teacherData,
			action.purchaseID,
			possibleDomain
		);
		return state;
	},
	[AT.PASSWORD_RESET_SUCCESS](vault, state, action) {
		const response = action.data;
		const {username, isFromTeacher} = action.payload;
		if (isFromTeacher) {
			state = setIn(
				state,
				[
					"officeStudentData",
					action.payload.classID,
					"students",
					action.payload.userID,
					"tempPassword",
				],
				response.tempPassword
			);
		} else {
			state = atUser(state).setIn("loginUsername", username);
			vault.emitEvent("tempPasswordRequested", response);
		}
		return state;
	},
	[AT.PASSWORD_RESET_FAIL](vault, state, action) {
		if (vault.api.isLocked()) {
			vault.emitEvent("showMessage", "tooManyRequests");
		} else {
			vault.emitEvent(
				"requestPasswordResetErrors",
				action.error,
				action.moreInfo
			);
			if (action.payload.isFromTeacher) {
				vault.emitEvent("showMessage", "requestPasswordResetFailure", {
					error: action.error,
				});
			}
		}
		return state;
	},

	[AT.IMITATE_STUDENT_REQUEST](vault, state, action) {
		vault.api.imitateStudent(action.student, action.method);
		return state;
	},
	[AT.IMITATE_STUDENT_SUCCESS](vault, state, action) {
		const res = action.data;
		if (res.success) {
			const studentID = action.payload.studentID;
			const student = atOfficeStudentData(state, studentID).get();
			const studentOnboardingProgress =
				student.eventsTriggered?.tutorials?.studentOnboardingV2;
			// If parent just completed onboarding and wants their child to take the placement test, just go there immediately!
			if (
				action.payload.method === "from-registration" &&
				OpsUser.shouldSeeStudentOnboarding(
					student,
					studentOnboardingProgress
				) &&
				studentOnboardingProgress?.currentStep !== "manualLevelSelection"
			) {
				state = atUser(state).set(user => OpsUser.loadPending(user));
				window.location = getOnePlacementUrl(student);
			} else if (action.payload.method === "from-account-deferral") {
				state = atUser(state).set(user => OpsUser.loadPending(user));
				window.location = getOnePlacementUrl(student);
			} else {
				// Otherwise, redirect to school page on success
				window.location = "/school";
			}
		} else {
			// Emit an event that the TopBar can listen for
			vault.emitEvent("imitateStudentFail", action.payload.studentID);
		}
		return state;
	},
	[AT.IMITATE_STUDENT_FAIL](vault, state, action) {
		// Emit an event that the TopBar can listen for
		vault.emitEvent("imitateStudentFail", action.payload.studentID);
		return state;
	},

	[AT.UPDATE_USER_REQUEST](vault, state, action) {
		vault.api.updateUser(action.user, action.baseUserData);
		return state;
	},
	[AT.UPDATE_USER_SUCCESS](vault, state, action) {
		const response = action.data;
		const baseUserData = action.baseUserData;
		if (response.success && baseUserData) {
			state = atUser(state).extendIn("model", baseUserData);
			if (baseUserData.avatar) {
				vault.emitEvent("updateAvatarParts", baseUserData.avatar);
			}
		}
		return state;
	},
	[AT.UPDATE_USER_FAIL](vault, state, action) {
		if (vault.api.isLocked()) {
			vault.emitEvent("showMessage", "tooManyRequests");
		} else {
			// No op
		}
		return state;
	},

	[AT.ACCOUNT_PASSWORD_SETUP_REQUEST](vault, state, action) {
		if (!state.connected) {
			vault.emitEvent("showMessage", "disconnected");
			return state;
		}
		const {email, password, code, codeType, passwordRequired} = action;

		let redirectURL = URLMake.forOffice();
		if (action.codeType === "parent") {
			redirectURL = URLMake.forParent();
		} else if (action.codeType === "staff") {
			redirectURL = URLMake.forSchool("accounts", "subscription", {}, true);
		}

		vault.api.setupAccountPassword(
			email,
			password,
			code,
			codeType,
			redirectURL,
			passwordRequired
		);
		return state;
	},

	[AT.ACCOUNT_PASSWORD_SETUP_SUCCESS](vault, state, action) {
		if (action.data.autologinURL) {
			global.window.location = action.data.autologinURL;
			return state;
		}

		global.window.location = action.redirectURL;
		return state;
	},

	[AT.ACCOUNT_PASSWORD_SETUP_FAIL](vault, state, action) {
		if (action.error === "E_INVALID_PASSWORD") {
			vault.emitEvent("setupAccountInvalidPassword", action.moreInfo);
		} else {
			vault.emitEvent("setupAccountPasswordFail", action.error);
		}
		return state;
	},

	[AT.EMAIL_CONFIRMATION_REQUEST](vault, state, action) {
		vault.api.resendEmailConfirmation(
			action.email,
			action.emailType,
			action.oldEmail
		);
		return state;
	},

	[AT.EMAIL_CONFIRMATION_SUCCESS](vault, state, action) {
		vault.emitEvent("emailConfirmationSent");
		return state;
	},

	[AT.EMAIL_CONFIRMATION_FAIL](vault, state, action) {
		vault.emitEvent("emailConfirmationSendError", action.error);
		return state;
	},

	[AT.UPDATE_LOGIN_USERNAME](vault, state, action) {
		state = atUser(state).setIn("loginUsername", action.username);
		vault.emitEvent("loginUsernameUpdated");
		return state;
	},

	[AT.VERSION_CHECK_WAITED](vault, state, action) {
		state = atUser(state).extend({newVersionWaited: true});
		return state;
	},

	[AT.VERSION_CHECK](vault, state, action) {
		const {frontendVersion, backendVersion} = action;

		// A dispatch of this action can sneak in between the reloadPage call below
		// and the page actually reloading. We prevent logging E_VERSION_STUCK in
		// that case with this boolean.
		const versionPendingRefresh = atUser(state).getIn("versionPendingRefresh");
		if (versionPendingRefresh) {
			return state;
		}

		const isValidVersion = BrowserUtil.checkVersion(
			backendVersion,
			frontendVersion
		);
		if (isValidVersion) {
			vault.storage.removeItem("versionWasStuck");
			vault.storage.removeItem("expectedVersion");
			return state;
		}
		// Rest of this function decides whether to force a refresh now.

		// Don't refresh if we haven't waited, see startReact.js
		const waited = atUser(state).getIn("newVersionWaited");
		if (!waited) {
			return state;
		}

		// Don't refresh the page if the user is in the middle of a result.
		const result = atCurrResult(state).get();
		if (result) {
			return state;
		}

		const lastExpectedVersion = vault.storage.getItem("expectedVersion");
		// If expected version is same as backend version, then that means user was
		// stuck on an old frontend version even after refresh.

		// This occurs if a page reload attempting to get the latest frontend
		// code fails. We get off the code paths below to avoid a refresh loop.
		// TODO: This happens to enough users that a message may be warranted...
		if (lastExpectedVersion && lastExpectedVersion === backendVersion) {
			logVersionStuck(vault.storage, frontendVersion, backendVersion);
			return state;
		}

		// All checks cleared; force a refresh.
		state = atUser(state).setIn("versionPendingRefresh", true);
		vault.storage.setItem("expectedVersion", backendVersion);
		vault.storage.removeItem("versionWasStuck");
		vault.reloadPage(true);

		return state;
	},

	[AT.LOGOUT_USER_REQUEST](vault, state, action) {
		const {logoutPage, forSwitch} = action;
		const currPage = atPage(state).get();
		let requestedURL = OpsPage.isStudentPage(currPage)
			? "/school/signin"
			: null;
		if (logoutPage) {
			requestedURL = URLMake.toURL(logoutPage);
		} else if (forSwitch) {
			requestedURL = URLMake.toURL(currPage);
		}

		Mixpanel.reset();

		// Just do a redirect.
		if (global.window) {
			let signoutURL = `/signout`;
			if (requestedURL) {
				signoutURL += `?requestedURL=${encodeURIComponent(requestedURL)}`;
			}
			global.window.location.href = signoutURL;
		}
		return state;
	},

	[AT.SESSION_EXPIRED](vault, state, action) {
		if (action.origType === AT.LOAD_USER_FAIL) {
			state = atUser(state).set(user =>
				OpsUser.loadError(user, "E_NOT_LOGGED_IN")
			);
		}

		if (OpsUser.isLoggedIn(state.user)) {
			state = atUser(state).setIn("sessionExpired", true);
			state = atUser(state).setIn("authenticated", false);
			if (OpsPage.isPageProtected(state.page)) {
				vault.emitEvent("sessionExpired");
			}
		}
		// Only set flag for definitely not logged in
		state = atUser(state).setIn("notLoggedIn", true);
		return state;
	},

	[AT.SESSION_NOT_PLAYING](vault, state, action) {
		const page = atPage(state).get();
		if (OpsPage.isPageProtected(page)) {
			vault.api.lock("E_SESSION_NOT_PLAYING");
			vault.emitEvent("sessionNotPlaying");
			vault.emitEvent("openedModalOnPage");
		}
		return state;
	},

	[AT.LOAD_USER_REQUEST](vault, state, action) {
		return atUser(state).set(user => {
			if (user.registered !== false) {
				return OpsUser.loadStart(user, vault.api);
			} else {
				return user;
			}
		});
	},

	[AT.LOAD_USER_SUCCESS](vault, state, action) {
		const response = action.data;
		const userResponse = response.user;
		const userID = userResponse.id;
		const imitatorUserID = response.user.imitatorUserID;
		const imitatorIdentifier =
			response.user.imitatorIdentifier ?? imitatorUserID;
		const imitatorRoles = response.user?.imitatorRoles || [];
		const userModel = APIFormat.convertUser(response.user);
		const page = atPage(state).get();

		// Remove flag to prevent silent login
		global.window && global.window.sessionStorage.removeItem("fa_silent");

		let requestedPage;
		state = atUser(state).set(u => {
			requestedPage = u.requestedPage;
			// Check for missing avatar data
			const avatarData = _.get(userModel, "avatar") || {};
			const displayName = _.get(userModel, "displayName");
			if (OpsUser.isStudent(u) && _.isEmpty(avatarData) && !displayName) {
				ErrorUtil.log(
					"E_NO_AVATAR",
					"Student user with no avatar data and no display name to fall back on for first letter backup avatar image.",
					{
						avatarData,
						userID,
					}
				);
			}

			return _.extend({}, OpsUser.loadSuccess(u), {
				// authenticated added here since this is no longer set on websocket
				// authentication
				authenticated: true,
				userID,
				imitatorUserID,
				imitatorIdentifier,
				imitatorRoles,
				platformUserID: userResponse.platformUserID,
				isRestricted: userResponse.isRestricted,
				isIplUser: userResponse.isIplUser,
				model: userModel,
				requestedPage: null,
				notLoggedIn: false,
			});
		});

		state = atUser(state).set(u => OpsUser.setUserJustLoggedIn(u, true));

		const user = atUser(state).get();

		// Track student login event (only for direct student logins on post-signin page)
		if (
			OpsUser.hasStudentRole(user) &&
			!imitatorUserID &&
			OpsPage.isPostLoginPage(page)
		) {
			let studentType = "b2c";
			if (OpsUser.isSchoolStudent(user)) {
				if (OpsUser.isAcademyUser(user)) {
					studentType = "academy";
				} else {
					studentType = "b2b-schools";
				}
			}
			Mixpanel.track("Student Login", {
				studentId: userID,
				username: userModel.username,
				student_type: studentType,
			});
		}

		// Set default redirect page (ex. for parent and teacher office page)
		if (!requestedPage && OpsPage.isStaticPage(page)) {
			requestedPage = OpsPage.getDefaultRequestedPage(user, vault.clock);
		}

		// If the user had requested a page earlier, send them there.
		// (We don't do this in the postupdate hook since that deliberately ignores
		// user.requestedPage, which we probably don't want to universally look up.)
		state = atPage(state).set(p =>
			OpsPage.getLoadedUserPage(p, requestedPage, vault.clock)
		);

		// If the page the user is on is an office page and they're a student, we
		// pop a modal to ask if they want to go to the trainer. Occurs in
		// UpdateOffice.augmentUserLoad.
		if (OpsUser.hasStudentRole(user)) {
			if (page.name === "404") {
				state = atPage(state).set(p => URLMake.state.forHome());
			}
		}

		// If the user is a teacher and they have unaccepted BA account setup
		// invites, direct them to the account setup page.
		const unacceptedTeacherInvites = OpsUser.getUnacceptedTeacherInvites(user);
		if (unacceptedTeacherInvites.length > 0) {
			state = atPage(state).set(p =>
				URLMake.state.forBAAccountSetup(
					_.get(user, "model.email"),
					_.get(user, "model.unacceptedTeacherInvites")[0]
				)
			);
		}

		// If the user has received a one off reward that should be shown, show the notification modal
		const userOneOffRewards = OpsUser.getOneOffRewards(user);
		// Get only the rewards that have not been shown yet and with reward amount greater than 0
		const notificationsToShow = userOneOffRewards.filter(
			reward => !reward.notificationShown && reward.bucks > 0
		);
		if (notificationsToShow.length > 0) {
			state = atUser(state).setIn("model.oneOffRewards", notificationsToShow);
			vault.emitEvent("showNotificationModal", "giftRewardForRiverCrossing");
			for (const notification of notificationsToShow) {
				vault.api.setOneOffRewardNotificationShown({
					notificationID: notification.id,
				});
			}
		}

		checkPrivacyChange(vault, state);
		updateAllSoundSettings(vault, state);
		vault.emitEvent("loadUserSuccess", user);

		state = atUser(state).set(u => OpsUser.updateSequenceData(u, state.page));
		state = atTutorial(state).set(t => OpsTutorial.updateTutorialData(state));
		if (
			OpsTutorial.shouldSeeTutorial(user) &&
			OpsTutorial.isTutorialActive(user) &&
			!OpsPage.isInStudentOnboarding(page)
		) {
			state = atPage(state).set(p => URLMake.state.forTutorial());
		}

		DataDogRUM.init(); // This is idempotent, so safe to call multiple times.
		DataDogRUM.setUser(user);
		Mixpanel.setUser(user.model);

		return state;
	},

	[AT.LOAD_USER_FAIL](vault, state, action) {
		state = atUser(state).set(user => OpsUser.loadError(user, action.error));
		return state;
	},

	[AT.LOAD_USER_PLATFORM_BASICS_REQUEST](vault, state, action) {
		// Load this information silently, we don't need to know load
		// status because nothing will be waiting on this info.
		const platformUserID = atUser(state).getIn("platformUserID");
		const userID = atUser(state).getIn("userID");
		vault.api.loadPlatformUserBasics(platformUserID, userID);
		return state;
	},

	[AT.LOAD_USER_PLATFORM_BASICS_SUCCESS](vault, state, action) {
		const {email, firstName, lastName, displayName, isEmailVerified} = _.get(
			action,
			"data.user",
			{}
		);
		const pendingEmail = _.get(action, "data.pendingEmail");
		state = atUser(state).setIn(["model", "email"], email);
		state = atUser(state).setIn(
			["model", "displayName"],
			displayName || firstName
		);
		state = atUser(state).setIn(["model", "firstName"], firstName);
		state = atUser(state).setIn(["model", "lastName"], lastName);
		state = atUser(state).setIn(["model", "newEmail"], pendingEmail);
		state = atUser(state).setIn(
			["model", "isEmailVerified"],
			!!isEmailVerified
		);
		state = atUser(state).setIn(
			["model", "platformEmailVerificationLoaded"],
			true
		);
		return state;
	},

	[AT.LOAD_USER_PLATFORM_BASICS_FAIL](vault, state, action) {
		// Do nothing, info here is non-critical.
		return state;
	},

	[AT.LOAD_USER_PLATFORM_PLACEMENT_REQUEST](vault, state, action) {
		return atUser(state).set(user => {
			Mixpanel.track("Student Onboarding: Placement Test API Load Request", {
				...getMixpanelDataFromUser(user),
			});
			const platformUserID = user.platformUserID;
			const userID = user.userID;
			return {
				...user,
				placementTest: LoadState.loadStart(user.placementTest, () => {
					return vault.api.loadPlatformUserPlacement(platformUserID, userID);
				}),
			};
		});
	},

	[AT.LOAD_USER_PLATFORM_PLACEMENT_SUCCESS](vault, state, action) {
		const response = action.data;
		state = atUser(state).set(user => {
			Mixpanel.track("Student Onboarding: Placement Test API Load Success", {
				placementTestInfo: user.placementTest,
				...getMixpanelDataFromUser(user),
			});
			return {
				...user,
				placementTest: {
					...LoadState.loadSuccess(user.placementTest),
					...response,
				},
			};
		});
		return state;
	},

	[AT.LOAD_USER_PLATFORM_PLACEMENT_FAIL](vault, state, action) {
		state = atUser(state).set(user => {
			Mixpanel.track("Student Onboarding: Placement Test API Load Error", {
				...getMixpanelDataFromUser(user),
			});
			return {
				...user,
				placementTest: LoadState.loadError(user.placementTest, action.error),
			};
		});
		return state;
	},

	[AT.ACCEPT_PRIVACY_REQUEST](vault, state, action) {
		if (atUser(state).getIn("isAcceptingPrivacy")) {
			//Already in progress
			return;
		}
		state = atUser(state).setIn("isAcceptingPrivacy", true);
		if (!_.isNumber(atUser(state).getIn("acceptPrivacyRetriesLeft"))) {
			atUser(state).setIn("acceptPrivacyRetriesLeft", 3);
		}
		vault.api.acceptPrivacy();
		return state;
	},

	[AT.ACCEPT_PRIVACY_SUCCESS](vault, state, action) {
		state = atUser(state).setIn("isAcceptingPrivacy", false);
		state = atUser(state).setIn(
			["model", "lastAcceptedPrivacyAt"],
			moment.now()
		);
		return state;
	},

	[AT.ACCEPT_PRIVACY_FAIL](vault, state, action) {
		state = atUser(state).setIn("isAcceptingPrivacy", false);
		//See if there are any retries left, if so retry
		const retriesLeft = atUser(state).getIn("acceptPrivacyRetriesLeft");
		if (retriesLeft > 0) {
			state = atUser(state).setIn("acceptPrivacyRetriesLeft", retriesLeft - 1);
			//Decrement retries & try again in 5 seconds
			vault.dispatchAfter(
				{
					type: AT.ACCEPT_PRIVACY_REQUEST,
				},
				5000
			);
		}
		return state;
	},

	[AT.SUPPRESS_OFFICE_CONFIRM_MODAL](vault, state, action) {
		const user = atUser(state).get();
		if (!state.connected || !_.get(user, "model")) {
			// Don't update if not connected or there's no user model.
			return state;
		}
		const shouldSuppress = _.get(action, "suppress", true);
		const previousMiscData = OpsUser.getMiscellaneousUserData(user);
		const suppressConfirm = OpsUser.getSuppressManualLockShowConfirm(user);
		if (!_.isEqual(shouldSuppress, suppressConfirm) || !suppressConfirm) {
			const userToUpdate = {
				id: user.userID,
				miscellaneous: _.extend({}, previousMiscData, {
					suppressManualLockShowConfirm: shouldSuppress,
				}),
			};
			vault.api.updateUser(DerivedBackend.computeUser(userToUpdate));
			state = atUser(state).set(usr =>
				OpsUser.setSuppressManualLockShowConfirm(usr, shouldSuppress)
			);
		}
		return state;
	},

	[AT.FORCE_LOAD_USER_PENDING](vault, state, action) {
		return atUser(state).set(user => OpsUser.loadPending(user));
	},

	[AT.STUDENT_ONBOARDING_PLACEMENT_COMPLETE_REQUEST](vault, state, action) {
		vault.api.updateStudentGradeFromPlacementTest(
			atUser(state).get().userID,
			action.payload
		);
		return atUser(state).set(user => OpsUser.loadPending(user));
	},

	[AT.STUDENT_ONBOARDING_PLACEMENT_COMPLETE_SUCCESS](vault, state, action) {
		// Force a refresh so new user data is set as expected.
		vault.reloadPage(true);
		let newState = atUser(state).set(user => LoadState.loadSuccess(user));
		newState = atUser(state).setIn(
			["model", "eventsTriggered", "tutorials", "studentOnboardingV2"],
			{currentPart: "completed", currentStep: "completed"}
		);
		return atPage(newState).set(p => {
			return URLMake.state.forChapterFromNumbers(
				action.payload.grade,
				action.payload.chapterIndex
			);
		});
	},

	[AT.STUDENT_ONBOARDING_PLACEMENT_COMPLETE_FAIL](vault, state, action) {
		return atUser(state).set(user => {
			return LoadState.loadError(user, action.error);
		});
	},

	[AT.LOAD_LOCKS_REQUEST](vault, state, action) {
		state = atUserOrClassLocks(state, action)
			.canCreate()
			.set(lov => {
				lov = action.classID
					? OpsLockOverrides.initClass(lov, action.classID)
					: OpsLockOverrides.initUser(lov, action.userID);
				lov = OpsLockOverrides.loadStart(lov, vault.api);
				return lov;
			});
		return state;
	},

	[AT.LOAD_LOCKS_SUCCESS](vault, state, action) {
		state = atUserOrClassLocks(state, action.payload).set(lov => {
			lov = OpsLockOverrides.loadSuccess(lov);
			lov = OpsLockOverrides.setLoadedData(lov, action.data);
			return lov;
		});
		return state;
	},

	[AT.LOAD_LOCKS_FAIL](vault, state, action) {
		state = atUserOrClassLocks(state, action.payload).set(lov => {
			lov = OpsLockOverrides.loadError(lov, action.error);
			return lov;
		});
		return state;
	},

	[AT.SET_LOCK_OVERRIDE_REQUEST](vault, state, action) {
		if (!state.connected) {
			vault.emitEvent("showOfficeDisconnectedModal", "setLockOverride");
			return state;
		}
		const user = atUser(state).get();
		const suppressConfirm = OpsUser.getSuppressManualLockShowConfirm(user);
		const isParent = OpsUser.hasParentRole(user);
		const showConfirm = !action.confirmed && !suppressConfirm && isParent;

		if (!showConfirm || action.newValue !== "locked") {
			state = atUserOrClassLocks(state, action).set(lov => {
				lov = OpsLockOverrides.setValue(
					lov,
					vault.api,
					action.objectType,
					action.objectID,
					action.newValue
				);
				return lov;
			});
			if (action.classID) {
				state = atAllLocks(state).set(OpsLockOverrides.resetAllUsers);
			}
		} else {
			const itemType = OpsLockOverrides.getDisplayedObjectType(
				action.objectTypeMessageOverride ?? action.objectType
			);
			vault.emitEvent("showConfirm", "manualLocking", {
				itemType,
				doNotShowAgainOption: true,
				onConfirmDispatch: _.extend({}, action, {confirmed: true}),
			});
		}
		return state;
	},

	[AT.SET_LOCK_OVERRIDE_SUCCESS](vault, state, action) {
		if (action.data.lockData) {
			state = atUserOrClassLocks(state, action.payload).set(lov => {
				lov = OpsLockOverrides.setLoadedData(lov, action.data);
				return lov;
			});
		}
		return state;
	},

	[AT.SET_LOCK_OVERRIDE_FAIL](vault, state, action) {
		state = atUserOrClassLocks(state, action.payload).set(lov => {
			lov = OpsLockOverrides.loadRestart(lov);
			return lov;
		});
		vault.emitEvent("setLockOverrideFail", action.error);
		return state;
	},

	[AT.UNLOCK_LAB_ITEM_BEFORE_LAB_UNLOCKED_REQUEST](vault, state, action) {
		if (!state.connected) {
			vault.emitEvent("showOfficeDisconnectedModal", "setLockOverride");
			return state;
		}
		// Toggle in data used to compact first call and confirm into single action
		const showConfirm = !action.confirmed;
		if (!showConfirm) {
			state = atUserOrClassLocks(state, action).set(lov => {
				lov = OpsLockOverrides.setValue(
					lov,
					vault.api,
					action.objectType,
					action.objectID,
					action.newValue
				);
				// also unlock the puzzle lab
				lov = OpsLockOverrides.setValue(
					lov,
					vault.api,
					"space",
					"lab",
					"unlocked"
				);
				return lov;
			});
		} else {
			const itemType = OpsLockOverrides.getDisplayedObjectType(
				action.objectType
			);
			// On confirm button, refire the action but this time with action.confirmed = true
			vault.emitEvent("showConfirm", "manualUnlockLabItemBeforeLabUnlocked", {
				itemType,
				onConfirmDispatch: _.extend({}, action, {confirmed: true}),
			});
		}
		return state;
	},

	[AT.UNLOCK_LAB_ITEM_BEFORE_LAB_UNLOCKED_SUCCESS](vault, state, action) {
		if (action.data.lockData) {
			state = atUserOrClassLocks(state, action.payload).set(lov => {
				lov = OpsLockOverrides.setLoadedData(lov, action.data);
				return lov;
			});
		}
		return state;
	},

	[AT.UNLOCK_LAB_ITEM_BEFORE_LAB_UNLOCKED_FAIL](vault, state, action) {
		state = atUserOrClassLocks(state, action.payload).set(lov => {
			lov = OpsLockOverrides.loadRestart(lov);
			return lov;
		});
		vault.emitEvent("setLockOverrideFail", action.error);
		return state;
	},

	[AT.UPDATE_PAGE_DATA_SUCCESS](vault, state, action) {
		return atUser(state).set(user =>
			OpsPageData.handleSaveSuccess(user, action.payload)
		);
	},
	[AT.UPDATE_PAGE_DATA_FAIL](vault, state, action) {
		return atUser(state).set(user =>
			OpsPageData.handleSaveFail(
				user,
				action.payload,
				action.error,
				vault.throttler
			)
		);
	},

	[AT.UPDATE_ACTIVITY_TIMES_SUCCESS](vault, state, action) {
		return atTimeTracking(state).set(tt =>
			TimeTracking.handleSaveSuccess(tt, action.payload)
		);
	},
	[AT.UPDATE_ACTIVITY_TIMES_FAIL](vault, state, action) {
		return atTimeTracking(state).set(tt =>
			TimeTracking.handleSaveFail(
				tt,
				action.payload,
				action.error,
				vault.throttler
			)
		);
	},

	[AT.SEND_BUG_REPORT_REQUEST](vault, state, action) {
		vault.emitEvent("closedModalOnPage");
		const diagnostics = vault.getDiagnostics({long: true});
		delete diagnostics.ongoingAction; //No need to record since it's this action
		let payload = setIn(action.payload, "diagnostics", diagnostics);
		payload = setIn(payload, "taskifyBugReport", FLAGS.canReportBugs);
		vault.api.sendBugReport(payload);
	},
	[AT.SEND_BUG_REPORT_SUCCESS](vault, state, action) {
		state = atUser(state).setIn("submittingBugReport", false);
		const problemCR = atCurrProblem(state);
		if (problemCR.exists()) {
			const reportData = {
				type: action.payload.type,
				diagnostics: action.payload.diagnostics,
			};
			state = problemCR.setIn("bugReports", br => {
				return (br || []).concat([reportData]);
			});
		}
		vault.emitEvent("bugReportSuccess");
		return state;
	},
	[AT.SEND_BUG_REPORT_FAIL](vault, state, action) {
		state = atUser(state).setIn("submittingBugReport", false);
		vault.emitEvent("showMessage", "failedBugReport", {
			errorCode: action.error,
		});
		vault.emitEvent("bugReportFail");
		return state;
	},

	[AT.UPDATE_SHOW_HELP](vault, state, action) {
		const user = atUser(state).get();
		if (!user.model) {
			return state;
		}
		vault.emitEvent("readAloudCancel");
		vault.emitEvent("openedModalOnPage");
		return atUser(state).setIn(["model", "showHelp"], false);
	},

	[AT.CLOSE_MODAL_IN_THEATER](vault, state, action) {
		vault.emitEvent("closedModalOnPage");
		return state;
	},

	[AT.LOAD_CHAPTER_REQUEST](vault, state, action) {
		const {userID, chapterID} = action;
		return atChapter(state, chapterID)
			.canCreate()
			.set(chapter => {
				if (GradeChapter.isUnderConstruction(chapterID, true)) {
					return LoadState.loadError(chapter, "E_OBJECT_DOES_NOT_EXIST");
				}
				if (!state.connected) {
					return LoadState.loadError(chapter, "E_NOT_CONNECTED");
				}
				return LoadState.loadStart(chapter, () =>
					vault.api.loadChapter(chapterID, userID)
				);
			});
	},
	[AT.LOAD_CHAPTER_ANTICIPATED_REQUEST](vault, state, action) {
		const {userID, chapterID} = action;
		return atChapter(state, chapterID)
			.canCreate()
			.set(chapter => {
				if (GradeChapter.isUnderConstruction(chapterID, true)) {
					return LoadState.loadError(chapter, "E_OBJECT_DOES_NOT_EXIST");
				}
				if (chapter.load !== "none" || !state.connected) {
					return chapter;
				}
				return LoadState.loadStart(chapter, () =>
					vault.api.loadChapter(chapterID, userID)
				);
			});
	},

	[AT.LOAD_CHAPTER_SUCCESS](vault, state, action) {
		const payload = action.payload;
		const response = action.data;

		const chapterID = payload.chapterID;
		const chapterModel = APIFormat.convertChapter(chapterID, response);
		const blockModels = APIFormat.convertChapterBlocks(chapterID, response);

		state = atChapter(state, chapterID)
			.canCreate()
			.set(chapter => {
				chapter = LoadState.loadSuccess(chapter);
				return setIn(chapter, "model", chapterModel);
			});

		const blockIDs = Object.keys(blockModels);
		state = atBlocks(state)
			.canCreate()
			.setKeys(blockIDs, (bl, id) => {
				bl = bl || OpsBlock.init(parseInt(id));
				return setIn(bl, "model", blockModels[id]);
			});

		return state;
	},
	[AT.LOAD_CHAPTER_FAIL](vault, state, action) {
		const payload = action.payload;
		return atChapter(state, payload.chapterID).set(chapter => {
			return LoadState.loadError(chapter, action.error);
		});
	},

	[AT.LOAD_LIBRARY_CHAPTERS_REQUEST](vault, state, action) {
		const {userID, chapterIDs} = action;

		const gradeNumber = GradeChapter.chapterIDToContext(chapterIDs[0])
			.gradeNumber;
		return atLibraryChaptersByGrade(state, gradeNumber)
			.canCreate()
			.set(lc => {
				if (!state.connected) {
					return LoadState.loadError(lc, "E_DISCONNECTED");
				}
				return LoadState.loadStart(lc, () => {
					return vault.api.loadLibraryChapters(chapterIDs, userID);
				});
			});
	},

	[AT.LOAD_LIBRARY_CHAPTERS_ANTICIPATED_REQUEST](vault, state, action) {
		const {userID, chapterIDs} = action;
		const gradeNumber = GradeChapter.chapterIDToContext(chapterIDs[0])
			.gradeNumber;
		return atLibraryChaptersByGrade(state, gradeNumber)
			.canCreate()
			.set(grade => {
				if (grade.load !== "none" || !state.connected) {
					return grade;
				}
				return LoadState.loadStart(grade, () =>
					vault.api.loadLibraryChapters(chapterIDs, userID)
				);
			});
	},

	[AT.LOAD_LIBRARY_CHAPTERS_SUCCESS](vault, state, action) {
		const {chapterIDs} = action.payload;
		const chapterModels = APIFormat.convertLibraryChapters(
			_.omit(action.data, ["success"])
		);
		const {gradeNumber} = GradeChapter.chapterIDToContext(chapterIDs[0]);

		state = atLibraryChaptersByGrade(state, gradeNumber)
			.canCreate()
			.setKeys(chapterIDs, (ch, cid) => {
				return OpsLibrary.initChapter(parseInt(cid), chapterModels[cid]);
			});
		state = atLibraryChaptersByGrade(state, gradeNumber).set(lc =>
			LoadState.loadSuccess(lc)
		);
		return state;
	},

	[AT.LOAD_LIBRARY_CHAPTERS_FAIL](vault, state, action) {
		const {chapterIDs} = action.payload;
		const {gradeNumber} = GradeChapter.chapterIDToContext(chapterIDs[0]);
		return atLibraryChaptersByGrade(state, gradeNumber)
			.canCreate()
			.set(lc => LoadState.loadError(lc, action.error));
	},

	[AT.LOAD_DEMO_CHAPTERS](vault, state, action) {
		const demoData = atDemoData(state).get();
		const loadedChapters = Object.values(
			atTempUser(state).getIn("chapters") || {}
		);
		const loadedChaptersIDs = LoadState.filterByStatus(
			loadedChapters,
			"done"
		).map(chapter => chapter.chapterID);
		const unloadedChaptersIDs = _.filter(
			demoData.chapterIDs,
			cid => !loadedChaptersIDs.includes(cid.toString())
		);
		_.forEach(unloadedChaptersIDs, chapterID => {
			state = OpsDemo.loadDemoChapter(state, chapterID);
		});
		return atTempUser(state).setIn("chapters", c => LoadState.loadSuccess(c));
	},

	[AT.LOAD_DEMO_CHAPTER](vault, state, action) {
		return OpsDemo.loadDemoChapter(state, action.chapterID);
	},

	[AT.LOAD_THEATER_SET_REQUEST](vault, state, action) {
		const {videoSetID, userID} = action;
		return atTheaterSet(state, videoSetID)
			.canCreate()
			.set(tvs => {
				if (!state.connected) {
					return LoadState.loadError(tvs, "E_DISCONNECTED");
				}
				return LoadState.loadStart(tvs, () => {
					return vault.api.loadTheaterSet(videoSetID, userID);
				});
			});
	},

	[AT.LOAD_THEATER_SET_SUCCESS](vault, state, action) {
		const {videoSetID} = action.payload;
		const setInfo = APIFormat.convertTheaterSet(action.data);

		state = atTheaterSet(state, videoSetID)
			.canCreate()
			.set(OpsTheater.initVideoSet(setInfo));
		state = atTheaterSet(state, videoSetID).set(tvs =>
			LoadState.loadSuccess(tvs)
		);
		return state;
	},

	[AT.LOAD_THEATER_SET_FAIL](vault, state, action) {
		const {videoSetID} = action.payload;

		return atTheaterSet(state, videoSetID).set(tvs =>
			LoadState.loadError(tvs, action.error)
		);
	},

	[AT.LOAD_DEMO_THEATER_SET](vault, state, action) {
		const {videoSetID} = action;
		const response = atDemoData(state).get().theaterSetResponse[videoSetID];
		const setInfo = APIFormat.convertTheaterSet({videoSet: response});

		state = atTheaterSet(state, videoSetID)
			.canCreate()
			.set(OpsTheater.initVideoSet(setInfo, true));
		state = atTheaterSet(state, videoSetID).set(tvs =>
			LoadState.loadSuccess(tvs)
		);
		return state;
	},

	[AT.LOAD_PROFILE_SLOT_REQUEST](vault, state, action) {
		const slot = Slots.table.profile(action.userID);
		return Slots.loadStart(state, slot, vault.api.loadSlot);
	},
	[AT.LOAD_PROFILE_SLOT_SUCCESS](vault, state, action) {
		return Slots.loadSuccess(state, action.slot, value => {
			return setIn(value, "data", action.data);
		});
	},
	[AT.LOAD_PROFILE_SLOT_FAIL](vault, state, action) {
		return Slots.loadError(state, action.slot, action.error);
	},

	[AT.LOAD_PROFILE_ACTIVITY_SLOT_REQUEST](vault, state, action) {
		const slot = Slots.table.profileActivity(action.userID, action.timestamp);
		return Slots.loadStart(state, slot, vault.api.loadSlot);
	},
	[AT.LOAD_PROFILE_ACTIVITY_SLOT_SUCCESS](vault, state, action) {
		return Slots.loadSuccess(state, action.slot, value => {
			return setIn(value, "data", action.data.calendar);
		});
	},
	[AT.LOAD_PROFILE_ACTIVITY_SLOT_FAIL](vault, state, action) {
		return Slots.loadError(state, action.slot, action.error);
	},

	[AT.LOAD_PROFILE_ACTIVITY_DAY_SLOT_REQUEST](vault, state, action) {
		const slot = Slots.table.profileActivityDay(
			action.userID,
			action.timestamp
		);
		return Slots.loadStart(state, slot, vault.api.loadSlot);
	},
	[AT.LOAD_PROFILE_ACTIVITY_DAY_SLOT_SUCCESS](vault, state, action) {
		return Slots.loadSuccess(state, action.slot, value => {
			return setIn(value, "data", action.data);
		});
	},
	[AT.LOAD_PROFILE_ACTIVITY_DAY_SLOT_FAIL](vault, state, action) {
		return Slots.loadError(state, action.slot, action.error);
	},

	[AT.LOAD_HOMEWORK_SLOT_REQUEST](vault, state, action) {
		const slot = Slots.table.homework(action.userID);
		return Slots.loadStart(state, slot, vault.api.loadSlot);
	},
	[AT.LOAD_HOMEWORK_SLOT_SUCCESS](vault, state, action) {
		return Slots.loadSuccess(state, action.slot, value => {
			return setIn(value, "data", action.data);
		});
	},
	[AT.LOAD_HOMEWORK_SLOT_FAIL](vault, state, action) {
		return Slots.loadError(state, action.slot, action.error);
	},

	[AT.ASSIGN_BLOCK_FOR_CLASS_REQUEST](vault, state, action) {
		const {classID, blockID, changeType} = action;
		vault.api.assignBlock({classID, blockID, changeType});
		state = atReportAssignedBlocks(state, classID).setIn(blockID, {
			isAssigned: changeType === "assign",
			assignedAt: new Date(),
		});
		const fauxAction = {
			classID,
			newValue: "unlocked",
			objectID: blockID,
			objectType: "block",
		};
		state = atUserOrClassLocks(state, fauxAction).setIn(
			["lockData", "blocks", blockID, "lockOverrideSource"],
			"assigned"
		);
		return state;
	},

	[AT.ASSIGN_BLOCK_FOR_CLASS_SUCCESS](vault, state, action) {
		const {blockID, classID} = action;
		const response = action.data;
		state = atReportAssignedBlocks(state, classID).setIn(blockID, response);
		return state;
	},

	[AT.ASSIGN_BLOCK_FOR_CLASS_FAIL](vault, state, action) {
		const assignedBlocksMultiSlot = MultiSlots.table.assignedBlocks();
		state = MultiSlots.clearSome(state, assignedBlocksMultiSlot, [
			action.classID,
		]);
		const students = _.get(action, ["moreInfo", "students"], []);
		state = atPage(state).setIn(["assignedBlocksModal"], {
			show: true,
			studentIDs: students,
			blockID: action.blockID,
			errorType: action.error,
			changeType: action.changeType,
		});
		state = atAllLocks(state).set(OpsLockOverrides.resetAllUsers);
		return state;
	},

	[AT.DISMISS_ASSIGNED_BLOCK_MODAL](vault, state, action) {
		return atPage(state).setIn(["assignedBlocksModal"], {show: false});
	},

	[AT.UNIMITATE_REQUEST](vault, state, action) {
		vault.api.unimitate(action.newURL);
		return state;
	},
	[AT.UNIMITATE_SUCCESS](vault, state, action) {
		const res = action.data;
		vault.cookie.setToken(res.token, res.tokenDuration);
		// forceLogout also begins the authenticate flow if a token is set.
		state = forceLogout(vault, state);
		if (action.newURL) {
			state = setIn(state, "redirectURL", action.newURL);
		}
		return state;
	},
	[AT.UNIMITATE_FAIL](vault, state, action) {
		vault.emitEvent("unimitateFail");
		return state;
	},

	[AT.SET_PAGE](vault, state, action) {
		// Note that several post update hooks do things for this action specifically.
		if (action.addToHistory) {
			vault.emitEvent("addToBrowserHistory");
		}

		return atPage(state).set(() => action.pageState);
	},

	[AT.SET_PAGE_VIDEO_INDEX](vault, state, action) {
		return atPage(state).setIn("videoIndex", action.videoIndex);
	},

	[AT.SHOW_MESSAGE](vault, state, action) {
		vault.emitEvent("showMessage", action.messageType, action.data || {});
		return state;
	},

	[AT.SHOW_CONFIRM](vault, state, action) {
		vault.emitEvent("closeSequenceSlideout");
		vault.emitEvent("showConfirm", action.messageType, action.data || {});
		return state;
	},

	[AT.SHOW_PROBLEM_SLIDER](vault, state, action) {
		vault.emitEvent("problemSliderMessage", {
			message: action.message,
			force: action.force,
		});
		return state;
	},

	[AT.TOGGLE_TOP_BAR](vault, state, action) {
		vault.emitEvent("toggleTopBar", action.isOpen);
		return state;
	},

	[AT.CONFIRM_PAGE_NAVIGATION](vault, state, action) {
		vault.emitEvent(
			"confirmPageNavigation",
			action.confirmType,
			action.href,
			action.data || {}
		);
		return state;
	},

	[AT.UPDATE_AVATAR_DATA](vault, state, action) {
		return atUser(state).setIn(
			["model", "avatar", "avatarImageData"],
			action.avatarImageData
		);
	},

	[AT.OPEN_SETTINGS_MODAL](vault, state, action) {
		vault.emitEvent("readAloudCancel");
		vault.emitEvent("openedModalOnPage");
		return state;
	},

	[AT.OPEN_LEADERBOARDS_MODAL](vault, state, action) {
		vault.emitEvent("openedModalOnPage");
		state = atLeaderboards(state).setIn("isShowingLeaderboardsModal", true);
		return state;
	},

	[AT.CLOSE_LEADERBOARDS_MODAL](vault, state, action) {
		state = atLeaderboards(state).setIn("isShowingLeaderboardsModal", false);
		return state;
	},

	[AT.FETCH_LEADERBOARDS_RANKINGS_REQUEST](vault, state, action) {
		return atLeaderboardsRankings(state, action)
			.canCreate()
			.set(rankings => {
				if (!state.connected) {
					return LoadState.loadError(rankings, "E_NOT_CONNECTED");
				}
				return LoadState.loadStart(rankings, () =>
					vault.api.fetchLeaderboardsRankings(action)
				);
			});
	},

	[AT.FETCH_LEADERBOARDS_RANKINGS_SUCCESS](vault, state, action) {
		const response = action.data;
		const formattedRankings = APIFormat.convertLeaderboardsRankings(response);
		state = atLeaderboardsRankings(state, action.payload).set(
			LoadState.loadSuccess(formattedRankings)
		);
		return state;
	},

	[AT.FETCH_LEADERBOARDS_RANKINGS_FAIL](vault, state, action) {
		const response = action.data;
		state = atLeaderboardsRankings(state, action.payload).set(
			LoadState.loadError(response, action.error)
		);
		return state;
	},

	[AT.OPEN_DEMO_LEARN_MORE_MODAL](vault, state, action) {
		vault.emitEvent("readAloudCancel");
		vault.emitEvent("openedModalOnPage");
		return state;
	},

	[AT.UPDATE_USER_SETTINGS](vault, state, action) {
		const user = atUser(state).get();
		if (!state.connected || !_.get(user, "model")) {
			// Don't update if not connected or there's no user model.
			return state;
		}
		const previousSettings = user.model.settings;
		const dataToUpdate = _.omit(action, ["type"]);
		const userToUpdate = {
			id: user.userID,
			settings: _.extend(previousSettings, dataToUpdate),
		};
		vault.api.updateUser(DerivedBackend.computeUser(userToUpdate));
		updateAllSoundSettings(vault, state);
		return atUser(state).extendIn(["model", "settings"], dataToUpdate);
	},

	[AT.UPDATE_TEMP_USER_SETTINGS](vault, state, action) {
		// send user settings to tempUser
		const dataToUpdate = _.omit(action, ["type"]);
		state = atTempUser(state).extendIn(["model", "settings"], dataToUpdate);
		updateAllSoundSettings(vault, state);
		return state;
	},

	[AT.CLOSE_DEMO_TUTORIAL](vault, state, action) {
		state = atDemo(state).setIn("showing", null);
		return state;
	},

	[AT.EXIT_DEMO_TUTORIAL](vault, state, action) {
		state = OpsDemo.exitTutorial(state, vault.storage);
		state = atDemo(state).setIn("showing", "exitingDemoTutorial");
		return state;
	},

	[AT.ATTEMPT_REPLAY_DEMO_LESSON](vault, state, action) {
		// used to fire demo post action hook
		return state;
	},

	[AT.SHOW_DEMO_EMAIL_SIGN_UP_MODAL](vault, state, action) {
		vault.emitEvent("showDemoEmailSignUpModal");
		state = atDemo(state).setIn("isShowingEmailModal", true);
		return state;
	},

	[AT.CLOSE_DEMO_EMAIL_SIGN_UP_MODAL](vault, state, action) {
		state = atDemo(state).setIn("isShowingEmailModal", false);
		return state;
	},

	[AT.RESTART_DEMO](vault, state, action) {
		state = OpsDemo.resetDemo(state, vault.storage);
		const ResultBanvas = Bundles.requireOpt("trainer", "LegacyResultBanvas");
		if (ResultBanvas) ResultBanvas.resetAll();
		return state;
	},

	[AT.CLOSE_SEQUENCE_SLIDEOUT](vault, state, action) {
		vault.emitEvent("closeSequenceSlideout");
		return state;
	},

	[AT.DEMO_SLIDER_OPENED](vault, state, action) {
		// used to fire demo post action hook
		return state;
	},

	[AT.VIEW_VIDEO_UNDER_CONSTRUCTION](vault, state, action) {
		// used to fire demo post action hook
		return state;
	},

	[AT.REPORT_BUG](vault, state, action) {
		vault.emitEvent("openedModalOnPage");
		vault.emitEvent("reportBug");
		return state;
	},

	[AT.UPDATE_BUG_SUBMITTING](vault, state, action) {
		return atUser(state).setIn(["submittingBugReport"], action.data.submitting);
	},

	[AT.SET_SHOW_BOOK](vault, state, action) {
		const {showBook, sectionID, gradeNumber} = action;
		// Need to remember if this is the first time the user has seen something in the library
		const hasSeenLibrary = atUser(state).getIn(
			["model", "activity", "hasSeenLibrary"],
			false
		);

		if (showBook) {
			// emit event that pauses playing video when a modal is opened
			vault.emitEvent("openedModalOnPage");
			// emit event that stops music when a reading modal is opened
			vault.emitEvent("openedReadingModal");
		} else {
			vault.emitEvent("closedModalOnPage");
			vault.emitEvent("closedReadingModal");
		}

		const isDemo = !!atPage(state).getIn(["demo"]);
		// showBookSectionID is only set when the book is opened on the chapter page
		if (showBook && !isDemo) {
			// If the user hasn't read a book before and they just opened the book
			// then we'll unlock the library and set a flag to later inform the user
			// the next time they visit the home page
			if (!hasSeenLibrary) {
				// If we are showing the book, we will mark that the user has seen the
				// library so that the button appears in the top bar
				state = atUser(state).setIn(
					["model", "activity", "hasSeenLibrary"],
					true
				);
				state = atUser(state).setIn(
					["model", "showJustUnlockedLibraryModal"],
					true
				);
			}
		}

		const page = atPage(state).get();
		const isChapterPage = PageShared.isTrainerChapterPage(page);
		if (isChapterPage && sectionID) {
			// if on chapter page, update avatar location
			// note that this updates on both open and close
			const chapterID = ChapterSection.getChapterIDFromSectionID(sectionID);
			const libraryChapter = atLibraryChapter(state, chapterID);
			const room = {
				isReading: true,
				bookSectionID: sectionID,
				chapterID,
				completed: OpsLibrary.librarySectionCompletelyRead(
					sectionID,
					libraryChapter.get()
				),
			};
			state = atUser(state).set(user =>
				OpsUser.updateLastPlayedDataWithRoom(user, room)
			);
			state = atChapterPath(state).set(cp =>
				OpsChapterPath.updateAvatarOverrideContext(cp, {forLastPlayed: true})
			);

			const userID = atUser(state).getIn("userID");
			OpsLibrary.updateChapterData(
				libraryChapter,
				userID,
				page,
				vault.api,
				vault.throttler
			);
		}

		// Update the page to reflect whether or not the book is open
		state = atPage(state).setIn("showBook", !!showBook);
		state = atPage(state).setIn("showBookSectionID", sectionID);

		// Just closed a book from the LibraryChapterPage
		if (OpsPage.isLibraryChapterPage(page) && !showBook) {
			state = atPage(state).set(p => {
				return URLMake.state.forLibraryFromNumbers(gradeNumber);
			});
		}
		return state;
	},

	[AT.SET_SHOW_THEATER_LESSON_MODAL](vault, state, action) {
		// Emit event that pauses playing video when a modal is opened.
		if (action.showTheaterToLessonModal) {
			vault.emitEvent("openedModalOnPage");
		} else {
			vault.emitEvent("closedModalOnPage");
		}
		//Update the page to reflect whether or not the theater to lesson modal is open
		return atPage(state).setIn(
			"showTheaterToLessonModal",
			!!action.showTheaterToLessonModal
		);
	},

	[AT.SET_SHOW_VIDEO](vault, state, action) {
		// We need this below to check if this is the first time
		// a user has closed a video and just unlocked the theater
		const hasSeenTheater = atUser(state).getIn(
			["model", "activity", "hasSeenTheater"],
			false
		);

		const isDemo = !!atPage(state).getIn(["demo"]);

		if (action.showVideo && !isDemo) {
			// If the user hadn't seen the theater before and they just opened
			// the video, we'll set a flag so the modal informing them they just
			// unlocked the theater will appear the next time they visit
			// the home page
			if (!hasSeenTheater) {
				state = atUser(state).setIn(
					["model", "activity", "hasSeenTheater"],
					true
				);
				state = atUser(state).setIn(
					["model", "showJustUnlockedTheaterModal"],
					true
				);
			}
		}
		//Update the page to reflect whether or not the video is open
		return atPage(state).setIn("showVideo", !!action.showVideo);
	},

	[AT.UPDATE_SHOW_NOTIFICATION](vault, state, action) {
		//Update the page to reflect whether or not the video is open
		return atPage(state).setIn(
			"isShowingNotification",
			!!action.isShowingNotification
		);
	},

	//TODO: demo - update these requests once we figure out how we want to send the emails
	[AT.DEMO_EMAIL_SIGN_UP_REQUEST](vault, state, action) {
		if (atTempUser(state).getIn("email")) {
			//Already submitting an email sign up
			return;
		}
		if (!state.connected) {
			// UPDATE THIS
			vault.dispatch({
				type: AT.SHOW_MESSAGE,
				messageType: "disconnected",
			});
			return state;
		}
		vault.api.emailSignUpForDemo(action.email, action.role);
		vault.emitEvent("emailSignUpStarted");
		return atTempUser(state)
			.canCreate()
			.setIn("email", true);
	},

	[AT.DEMO_EMAIL_SIGN_UP_SUCCESS](vault, state, action) {
		state = atTempUser(state).setIn("email", false);
		vault.emitEvent("emailSignUpComplete", "signUpSuccess");
		Analytics.sendEmailSignup();
		state = OpsMarketing.addEventToQueue(
			state,
			"MailingListSignUp",
			action.data.mailingListID,
			vault.cookie,
			vault.throttler,
			vault.clock
		);
		return state;
	},

	[AT.DEMO_EMAIL_SIGN_UP_FAIL](vault, state, action) {
		state = atTempUser(state).setIn("email", false);
		if (action.error === "E_TOO_MANY_REQUESTS") {
			vault.emitEvent("emailSignUpEnd");
			return state;
		}
		let nextStep = "signUpFail";
		if (action.error === "E_DUPLICATE_EMAIL") {
			nextStep = "duplicateEmail";
		}
		vault.emitEvent("emailSignUpComplete", nextStep);
		return state;
	},

	[AT.START_OFFICE_LOGIN](vault, state, action) {
		const user = atUser(state).get();
		if (OpsUser.isDefinitelyNotLoggedIn(user) && global.window) {
			const origURL = encodeURIComponent(URLMake.forPageState(state.page));
			let toURL = `/signin-redirect/${origURL}`;
			if (OpsPage.isPageNotForDefaultSignin(state.page)) toURL = "/signin";
			const email = action.opts?.email;
			if (email) {
				const prefill = toURL.includes("?") ? "&" : "?";
				toURL += `${prefill}prefill=${encodeURIComponent(email)}`;
			}
			global.window.location = toURL;
		}
		return state;
	},

	[AT.LOAD_CMS_INFO_PAGE_REQUEST](vault, state, action) {
		const {path} = action;
		return atCMSInfoPage(state, path)
			.canCreate()
			.set(file => {
				if (!state.connected) {
					return LoadState.loadError(file, "E_NOT_CONNECTED");
				}
				return LoadState.loadStart(file, () => vault.api.loadCMSInfoPage(path));
			});
	},
	[AT.LOAD_CMS_INFO_PAGE_SUCCESS](vault, state, action) {
		const payload = action.payload;
		const response = action.data;

		const path = _.get(payload, "path");

		state = atCMSInfoPage(state, path)
			.canCreate()
			.set(file => {
				file = LoadState.loadSuccess(file);
				return setIn(file, "model", response);
			});
		return state;
	},
	[AT.LOAD_CMS_INFO_PAGE_FAIL](vault, state, action) {
		const payload = action.payload;
		state = atCMSInfoPage(state, payload.path).set(file => {
			return LoadState.loadError(file, action.error);
		});
		return state;
	},
	[AT.LOAD_LAB_TYPE_REQUEST](vault, state, action) {
		const {userID, typeID} = action;
		return atLabType(state, typeID)
			.canCreate()
			.set(labType => {
				if (!state.connected) {
					return LoadState.loadError(labType, "E_NOT_CONNECTED");
				}
				return LoadState.loadStart(labType, () =>
					vault.api.loadLabType(typeID, userID)
				);
			});
	},
	[AT.LOAD_LAB_TYPE_SUCCESS](vault, state, action) {
		const payload = action.payload;
		const response = action.data;

		const typeID = payload.typeID;
		const labTypeModel = APIFormat.convertLabType(typeID, response);
		const blockModels = APIFormat.convertLabTypeBlocks(typeID, response);

		state = atLabType(state, typeID)
			.canCreate()
			.set(labType => {
				labType = LoadState.loadSuccess(labType);
				return setIn(labType, "model", labTypeModel);
			});

		const blockIDs = Object.keys(blockModels);
		state = atLabBlocks(state)
			.canCreate()
			.setKeys(blockIDs, (bl, id) => {
				bl = bl || OpsBlock.init(parseInt(id), "lab");
				return setIn(bl, "model", blockModels[id]);
			});

		return state;
	},
	[AT.LOAD_LAB_TYPE_FAIL](vault, state, action) {
		const payload = action.payload;
		return atLabType(state, payload.typeID).set(labType => {
			return LoadState.loadError(labType, action.error);
		});
	},

	[AT.UPDATE_TUTORIAL_STEP](vault, state, action) {
		const {tutorialType, step} = action;
		return atUser(state).setIn(
			["model", "eventsTriggered", "tutorials", tutorialType],
			step
		);
	},

	[AT.UPDATE_USER_EVENTS_TRIGGERED](vault, state, action) {
		const {propValue, pathArray, callback} = action;

		const newState = atUser(state).setIn(
			["model", "eventsTriggered"].concat(pathArray),
			propValue
		);

		if (
			pathArray.length === 2 &&
			pathArray[0] === "notifications" &&
			pathArray[1] === "placementTestInviteModal" &&
			propValue === "shown"
		) {
			vault._placementTestInviteEventEmitted = false;
		}

		OpsPageData.savePageDataNow(atUser(newState).get(), vault.api);

		if (callback) {
			callback();
		}

		return newState;
	},

	[AT.LOG_MARKETING_EVENTS_SUCCESS](vault, state, action) {
		state = atMarketing(state).set(marketing =>
			OpsMarketing.handleSaveSuccess(marketing, action.payload)
		);
		return state;
	},
	[AT.LOG_MARKETING_EVENTS_FAIL](vault, state, action) {
		state = atMarketing(state).set(marketing =>
			OpsMarketing.handleSaveFail(
				marketing,
				action.payload,
				action.error,
				vault.throttler
			)
		);
		return state;
	},

	[AT.LOG_MARKETING_SOURCE_SUCCESS](vault, state, action) {
		// No-op for now
		return state;
	},
	[AT.LOG_MARKETING_SOURCE_FAIL](vault, state, action) {
		// TODO: set up retry attempts
		if (!LoadState.shouldRetry(action.error)) {
			ErrorUtil.log(
				"E_LOG_MARKETING_SOURCE_FAIL",
				"Failed to log a marketing source",
				action.payload
			);
		}
		return state;
	},

	[AT.READ_ALOUD](vault, state, action) {
		const {command} = action;
		// Need way to report back when speech is FINISHED so we can turn the pause button
		// back into a play button
		switch (command) {
			case "play":
				const problem = atCurrProblem(state).get();
				const result = atCurrResult(state).get();
				const page = atPage(state).get();
				const OpsProblem = Bundles.requireOpt("trainer", "OpsProblem");
				const OpsResult = Bundles.requireOpt("trainer", "OpsResult");
				const {showInstructions = false} = page;
				let text = "",
					type = null;

				const isOnTestOverview =
					(result.showOverview || result.showTestPart1Decision) &&
					result.isOnFinal;

				if (isOnTestOverview) {
					text = OpsPage.getTestOverviewReadAloudText();
				} else if (showInstructions) {
					const user = atUser(state).get();
					const block = atCurrBlock(state).get();
					({text, type} = OpsBlock.getReadAloudTextInfo(
						user,
						block,
						result,
						problem
					));
				} else if (problem && OpsProblem) {
					({text, type} = OpsProblem.getReadAloudTextInfo(problem));
				}

				if ((text && type && OpsResult) || isOnTestOverview) {
					state = atCurrResult(state).set(res =>
						OpsResult.recordReadAloudClick(page, res, problem, type)
					);
					vault.emitEvent("readAloudSpeak", text);
				}
				break;
			case "resume":
				vault.emitEvent("readAloudResume");
				break;
			case "pause":
				vault.emitEvent("readAloudPause");
				break;
			case "cancel":
				vault.emitEvent("readAloudCancel");
				break;
			default:
				ErrorUtil.log(
					"E_UNRECOGNIZED_READALOUD_ACTION",
					`Tried to call invalid Read Aloud action ${command}`
				);
				break;
		}

		return state;
	},
	[AT.UPDATE_READ_ALOUD_STATUS](vault, state, action) {
		state = atReadAloud(state)
			.canCreate()
			.set(ras => {
				ras.isPlaying = !!action.playing;
				ras.isPaused = !!action.paused;
				return ras;
			});
		return state;
	},

	[AT.LOAD_NEXT_BLOCK](vault, state, action) {
		const {nextBlock, showBook, showVideo} = action;
		state = atPage(state).set(URLMake.state.forBlock(nextBlock));
		state = atPage(state).setIn("showBook", !!showBook);
		state = atPage(state).setIn("showVideo", !!showVideo);
		return state;
	},

	[AT.CHECK_USER_EXISTS_REQUEST](vault, state, action) {
		if (!state.connected) {
			vault.emitEvent("showMessage", "disconnected");
			return state;
		}
		const payload = _.pick(action, ["email", "password"]);
		vault.api.checkUserExists(payload);
		return state;
	},

	[AT.CHECK_USER_EXISTS_SUCCESS](vault, state, action) {
		const response = action.data;
		if (response.userExists) {
			vault.emitEvent(
				response.includesPassword
					? "accountAlreadyExists"
					: "userExistsWithEmail"
			);
		} else {
			vault.emitEvent(
				response.includesPassword
					? "accountDoesNotExist"
					: "userDoesNotExistWithEmail"
			);
		}
		return state;
	},

	[AT.CHECK_USER_EXISTS_FAIL](vault, state, action) {
		const {error, moreInfo} = action;
		if (error === "E_INVALID_PASSWORD") {
			// This is kind of weird that we're sticking in a "manual"
			// validation error here after checking password on Platform.
			// But if we don't do this, we'll have to have our own version
			// of password check that mimics Platform, which seems
			// superfluous. Or, handle the error differently for some types
			// of invalid passwords than others, which is behaviorly very
			// weird.
			state = atCart(state).setIn(
				["form", "validation", "account", "errorTypes"],
				{
					password: {type: moreInfo},
				}
			);
			vault.emitEvent("verifyNewAccountNotReady");
		} else {
			vault.emitEvent("showOfficeModal", "checkEmailAddressFail");
			vault.emitEvent("verifyNewAccountFail");
		}

		return state;
	},

	[AT.DEBUG_HIDE_DEV_WINDOWS](vault, state, action) {
		return setIn(state, "devWindowsAreHidden", true);
	},

	[AT.DEBUG_RERENDER](vault, state, action) {
		return _.clone(state);
	},

	[AT.DEBUG_INCREMENT_TIME](vault, state, action) {
		return TimeTracking.debugAddTime(state, action.amountMs);
	},

	[AT.DEBUG_SAVE_TIME_NOW](vault, state, action) {
		const tt = atTimeTracking(state).get();
		return vault.throttler.runNow("activityTimes", tt);
	},

	[AT.TRIGGER_DEV_ENDPOINT](vault, state, action) {
		vault.api.triggerDevEndpoint();
		return state;
	},

	[AT.DEV_ENDPOINT_SUCCESS](vault, state, action) {
		// eslint-disable-next-line no-console
		console.log(JSON.stringify(action.data, null, "\t"));

		return state;
	},

	[AT.DEBUG_IMPORT_TRAINER_DATA_FOR_USER_REQUEST](vault, state, action) {
		vault.api.debugImportTrainerDataForUser(action.userID);
		return state;
	},

	[AT.DEBUG_IMPORT_TRAINER_DATA_FOR_USER_FAIL](vault, state, action) {
		vault.emitEvent("trainerDataImportFailed");
		return state;
	},

	[AT.DEBUG_IMPORT_TRAINER_DATA_FOR_USER_SUCCESS](vault, state, action) {
		vault.emitEvent("trainerDataImportComplete");
		return state;
	},

	[AT.DEBUG_IMPORT_TRAINER_DATA_FOR_CLASS_REQUEST](vault, state, action) {
		vault.api.debugImportTrainerDataForClass(action.classID);
		return state;
	},

	[AT.DEBUG_IMPORT_TRAINER_DATA_FOR_CLASS_FAIL](vault, state, action) {
		vault.emitEvent("trainerDataImportFailed");
		return state;
	},

	[AT.DEBUG_IMPORT_TRAINER_DATA_FOR_CLASS_SUCCESS](vault, state, action) {
		vault.emitEvent("trainerDataImportComplete");
		return state;
	},

	[AT.ADD_ONE_OFF_REWARD_REQUEST](vault, state, action) {
		vault.api.addOneOffReward(action.payload);
		return state;
	},

	[AT.ADD_ONE_OFF_REWARD_SUCCESS](vault, state, action) {
		vault.emitEvent("oneOffRewardAddSuccess");
		return state;
	},

	[AT.ADD_ONE_OFF_REWARD_FAIL](vault, state, action) {
		vault.emitEvent("oneOffRewardAddFailed");
		return state;
	},

	[AT.SET_ONE_OFF_REWARD_NOTIFICATION_SHOWN_REQUEST](vault, state, action) {
		vault.api.setOneOffRewardNotificationShown(action.payload);
		return state;
	},

	[AT.SET_ONE_OFF_REWARD_NOTIFICATION_SHOWN_SUCCESS](vault, state, action) {
		return state;
	},

	[AT.SET_ONE_OFF_REWARD_NOTIFICATION_SHOWN_FAIL](vault, state, action) {
		return state;
	},

	[AT.DEV_ADD_NEW_STUDENT_REQUEST](vault, state, action) {
		vault.api.devAddNewStudent(action.payload);
		return state;
	},

	[AT.DEV_ADD_NEW_STUDENT_SUCCESS](vault, state, action) {
		vault.emitEvent("devAddNewStudentSuccess");
		return atUser(state).set(user => {
			return OpsUser.loadRestart(user, vault.api);
		});
	},

	[AT.DEV_ADD_NEW_STUDENT_FAIL](vault, state, action) {
		vault.emitEvent("devAddNewStudentFail");
		return state;
	},
};

const updateAllSoundSettings = function(vault, state) {
	updateSoundsEnabled(vault, state);
	updateSoundsVolume(vault, state);
	updateMusicVolume(vault, state);
	// Don't play music in idle tabs.
	if (state.idle) {
		vault.sounds.setMusicMuted(true);
	} else {
		updateMusicEnabled(vault, state);
	}
};

const updateSoundsEnabled = function(vault, state) {
	const user = _.get(state, "page.demo") ? atTempUser(state) : atUser(state);
	vault.sounds.setAllSoundsMuted(!OpsUser.areSoundsEnabled(user.get()));
};

const updateMusicEnabled = function(vault, state) {
	const user = _.get(state, "page.demo") ? atTempUser(state) : atUser(state);
	vault.sounds.setMusicMuted(!OpsUser.isMusicEnabled(user.get()));
};

const updateSoundsVolume = function(vault, state) {
	const user = _.get(state, "page.demo") ? atTempUser(state) : atUser(state);
	vault.sounds.setAllSoundsVolume(OpsUser.getSoundsVolume(user.get()));
};

const updateMusicVolume = function(vault, state) {
	const user = _.get(state, "page.demo") ? atTempUser(state) : atUser(state);
	vault.sounds.setMusicVolume(OpsUser.getMusicVolume(user.get()));
};

const checkPrivacyChange = function(vault, state) {
	const user = atUser(state).get();
	if (user.load === "done") {
		//Check if the user needs to be alerted to privacy changes
		if (
			moment(Constants.PRIVACY_LAST_UPDATED).isAfter(
				moment(user.model.lastAcceptedPrivacyAt)
			)
		) {
			vault.emitEvent("showPrivacyChangeAlert", Constants.PRIVACY_LAST_UPDATED);
		}
	}
};

// Various steps that need to happen before resetting a vault state due to a
// logout or whatever.
const doResetMaintenance = function(vault, state) {
	Mixpanel.reset();
	// Flush all updates, including activity, results, etc.
	// TODO: For a logout, we might never hear back from anything as we are about
	// to destroy the socket. We may want a more complex system of logging with
	// userID attached.
	vault.throttler.flushAll();
	// Pending API requests are invalid, and if a response sneaks in before we
	// finish recreating the socket, it might error (T9656). Make sure to do
	// this before user logout.
	vault.api.ignorePendingAPIResponses();
	vault.emitEvent("resetVaultState", atUser(state).get());
	return state;
};

// Handles triggering a logout due to server reasons rather than user request.
const forceLogout = function(vault, state) {
	const page = atPage(state).get();
	state = initializeVaultAndSession(vault, state, {
		pageState: OpsPage.getLoggedOutPage(page),
	});
	return state;
};

// One-time setup steps that should run even if the server sent vault state.
const initializeVault = function(vault, state) {
	OpsPageData.setupThrottlerInVault(vault);
	TimeTracking.setupThrottlerInVault(vault);
	OpsMarketing.setupThrottlerInVault(vault);
	updateAllSoundSettings(vault, state);
	return state;
};

const initializeVaultAndSession = function(vault, state, opts) {
	if (opts.isFirstAction) {
		state = initializeVault(vault, state);
	} else {
		state = doResetMaintenance(vault, state);
	}

	if (opts.pageState) {
		state = setIn(state, "page", opts.pageState);
	}
	if (opts.shouldReset) {
		state = resetVaultState(vault, state);
	}
	state = startUserLoad(vault, state);

	return state;
};

const startUserLoad = function(vault, state) {
	state = atUser(state).set(user => {
		return OpsUser.loadStart(user, vault.api);
	});
	// This is run here in addition to LOAD_USER_SUCCESS since that may occur
	// on the server side & this needs to emit an event.
	checkPrivacyChange(vault, state);
	return state;
};

const restartUserLoad = function(vault, state) {
	state = atUser(state).set(user => {
		return OpsUser.loadRestart(user, vault.api);
	});
	checkPrivacyChange(vault, state);
	return state;
};

//
// Pre-update hooks
//

const lockAPIOnTooManyRequests = function(vault, state, action) {
	if (action.error === "E_TOO_MANY_REQUESTS") {
		if (!vault.api.isLocked()) {
			ErrorUtil.log(
				"E_TOO_MANY_REQUESTS",
				"Server banned this session for making too many requests"
			);
			const messageType = OpsUser.isLoggedIn(atUser(state).get())
				? "userRateLimit"
				: "tooManyRequests";
			if (OpsPage.isOfficePage(atPage(state).get())) {
				vault.emitEvent("showOfficeModal", messageType);
			} else {
				vault.emitEvent("showMessage", messageType);
			}
		}
		vault.api.lock("E_TOO_MANY_REQUESTS");
	}
};

//
// The post-update hooks
//

const validatePage = function(vault, oldState, newState, action) {
	if (global.PLAY_PROBLEM_DATA) {
		return newState;
	}
	let page = atPage(newState).get();
	let user = atUser(newState).get();
	let justRedirectedToHomeOnError = false;
	const origURL = encodeURIComponent(URLMake.forPageState(newState.page));
	// We're hitting SESSION_EXPIRED which is only called if it fails
	// authentication for any endpoint.
	const isDefinitelyNotLoggedIn = OpsUser.isDefinitelyNotLoggedIn(user);
	if (isDefinitelyNotLoggedIn && OpsPage.isPageProtected(page)) {
		// If on a protected page, it will redirect to Plog login.
		if (OpsPage.isPostLoginPage(page)) {
			// Redirecting to sign-in in this case is likely to be a redirect loop.
			// Prevent that.
			// TODO: what to redirect to?
			newState = atPage(newState).set(p => {
				return URLMake.state.forHome();
			});
			justRedirectedToHomeOnError = true;
			page = atPage(newState).get();
		} else if (global.window) {
			if (OpsPage.isSignedInStudentPage(page)) {
				global.window.location = `/school/signin-redirect/${origURL}`;
			} else {
				global.window.location = `/signin-redirect/${origURL}`;
			}
		}
		return newState;
	}

	// If original page is a page for not logged-in users
	if (OpsUser.isLoggedIn(user)) {
		if (OpsPage.isPageForLogoutPrompt(page)) {
			if (OpsPage.isOfficePage(page)) {
				vault.emitEvent("showOfficeModal", "mustSignout", {
					cannotClose: true,
				});
			} else {
				vault.emitEvent("showMessage", "mustSignout", {});
			}
		} else if (
			OpsPage.isPageForNotLoggedIn(page) ||
			(OpsPage.isAccountSetupPage(page) &&
				_.get(user, "model.email").toLowerCase() !== page.email.toLowerCase())
		) {
			// If they are on page that's for not logged in or on account
			// setup page where their email doesn't match setup email.
			global.window.location = `/signout?requestedURL=${origURL}`;
			return newState;
		}
	}

	// Clear last played data if result is broken or locked (to avoid restricting users in check below)
	newState = checkForBadTestInProgress(newState);
	user = atUser(newState).get();
	// On a restricted page? (e.g. on a teacher page as a student, beastbuilder page, etc.)
	newState = atPage(newState).set(p => OpsPage.checkPageRestriction(user, p));
	// In demo and on a block, video, or library chapter not included in demo?
	const demoData = atDemoData(newState).get();
	newState = atPage(newState).set(p =>
		OpsDemo.checkDemoRestriction(demoData, p, OpsUser.isLoggedIn(user))
	);
	page = atPage(newState).get();

	const oldPage = atPage(oldState).get();

	// If a user tries to go to the student tutorial (/school/tutorial)
	// and they either shouldn't see it or the tutorial
	// isn't active for them (they have previously completed it or the tutorial
	// is disabled) then they are sent to the home page (/school)
	if (
		!(oldPage && OpsPage.isInStudentTutorial(oldPage)) &&
		OpsPage.isInStudentTutorial(page) &&
		(!OpsTutorial.isTutorialActive(user) ||
			!OpsTutorial.shouldSeeTutorial(user))
	) {
		newState = atPage(newState).set(p => {
			return URLMake.state.forHome();
		});
		page = atPage(newState).get();
	}

	// If a user tries to go to the student onboarding flow (/school/onboarding)
	// and they shouldn't see it, send them to the home page (/school)
	if (
		!(oldPage && OpsPage.isInStudentOnboarding(oldPage)) &&
		OpsPage.isInStudentOnboarding(page) &&
		!OpsUser.shouldSeeStudentOnboarding(user)
	) {
		newState = atPage(newState).set(p => {
			return URLMake.state.forHome();
		});
		page = atPage(newState).get();
	}

	const isStudent = OpsUser.hasStudentRole(user);
	const canPlay = OpsUser.hasTrainerPermission(user);
	const canViewTrainer = canPlay || isStudent || page.demo;

	// Student needs to complete onboarding before playing
	if (
		!OpsPage.isInStudentOnboarding(page) &&
		OpsUser.shouldSeeStudentOnboarding(user) &&
		!PageShared.isOfficePage(page)
	) {
		newState = atPage(newState).set(p => {
			return URLMake.state.forHomeOnboarding();
		});
		page = atPage(newState).get();
	}
	// Simplified navigation... remove the campus view!
	else if (
		canViewTrainer &&
		shouldRemoveCampusView(page) &&
		!OpsPage.isOfficePage(page) &&
		OpsPage.isHomeMain(page) &&
		!justRedirectedToHomeOnError
	) {
		const chapterID = OpsChapterPath.getLastPlayedData(user).chapterID;
		newState = atPage(newState).set(p => {
			return URLMake.state.forChapterFromID(chapterID);
		});
		page = atPage(newState).get();
	}

	return newState;
};

const checkForBadTestInProgress = function(state) {
	const user = atUser(state).get();
	const block = OpsUser.getTestInProgressBlock(user, state);
	const broken = _.get(block, "model.underConstruction");
	const locked = block && block.load === "done" && !OpsBlock.isUnlocked(block);
	if (broken || locked) {
		state = OpsUser.clearTestInProgress(state);
	}
	const page = atPage(state).get();
	if (
		OpsPage.isPageForBookTestBlock(page) &&
		OpsPage.isCurrentlyTakingBookTest(user, page) &&
		OpsUser.bookTestAssignedNow(user)
	) {
		state = OpsUser.clearTestInProgress(state);
	}
	return state;
};

//This contains the small checks that must occur on page changes
const checkPageChange = function(vault, oldState, newState, action) {
	const oldPage = atPage(oldState).get();
	const newPage = atPage(newState).get();

	if (!_.isEqual(oldPage, newPage)) {
		const oldReqPage = atUser(oldState).getIn("requestedPage");
		// If the user originally had a requestedPage, clear it.
		if (oldReqPage) {
			newState = atUser(newState).setIn("requestedPage", null);
		}

		// set temporary flag to indicate whether user has just logged in
		if (oldPage) {
			newState = setIn(newState, "previousPage", oldPage);
			newState = atUser(newState).set(u =>
				OpsUser.setUserJustLoggedIn(u, !OpsPage.isStudentPage(oldPage))
			);
		}
		// Update the sequenceData on valid page changes
		newState = atUser(newState).set(user =>
			OpsUser.updateSequenceData(user, newPage)
		);
		// Cancel any currently running speech
		vault.emitEvent("readAloudCancel");
	}
	return newState;
};

const checkCommonAPIError = function(vault, oldState, newState, action) {
	if (action && action.error === "E_NO_IMITATING") {
		vault.emitEvent("noImitating", action.url);
	}
};

// Stop music when on an office page.
const stopSounds = function(vault, oldState, newState, action) {
	if (
		_.get(action, "type") !== AT.STORAGE_UPDATED &&
		PageShared.isOfficePage(newState.page)
	) {
		Sounds.stopMusic(true);
	}
	return newState;
};

// Do things when a user increases in level via XP increase.
// XP increase can happen in trainer, library, theater
const checkLevelUp = function(vault, oldState, newState, action) {
	const oldXP = atUser(oldState).getIn(["model", "xp"]);
	const newXP = atUser(newState).getIn(["model", "xp"]);
	if (newXP > oldXP) {
		// Add bucks for leveling up
		const addedBucks = TrainerCore.getBucksFromXP(oldXP, newXP);
		if (addedBucks) {
			newState = atUser(newState).set(u => OpsUser.addBucks(u, addedBucks));
		}
	}
	const oldLevel = TrainerCore.getLevel(oldXP);
	const newLevel = TrainerCore.getLevel(newXP);
	if (newLevel > oldLevel) {
		newState = OpsLab.unlockOnLeveling(newState, newLevel);
	}
	return newState;
};

const checkScreen = function(vault, oldState, newState, action) {
	if (newState.screenWarning === "ignore") {
		return newState;
	}
	const newPage = atPage(newState).get();
	const user = atUser(newState).get();
	// Add a warning for people going to trainer/demo on phones
	const screenWarning = getScreenWarning();
	if (screenWarning === "phone" && newPage.demo) {
		newState = setIn(newState, "screenWarning", "demo");
	} else if (screenWarning === "phone" && newPage.name === "office.main") {
		newState = setIn(newState, "screenWarning", "ignore");
	} else if (
		screenWarning === "phone" &&
		OpsPage.isStudentPage(newPage) &&
		(user.load !== "done" || OpsUser.hasStudentRole(user))
	) {
		// Students always get the onboarding-styled warning on small screens,
		// including while user data is still loading (avoids a loading spinner).
		newState = setIn(newState, "screenWarning", "student-phone");
	} else if (
		screenWarning &&
		(OpsPage.isStudentPage(newPage) || newPage.demo)
	) {
		let isOngoingGame = false;
		const result = atCurrResult(newState).get();
		// Check result here so we don't switch to screen warning during an ongoing game.
		if (
			result &&
			ResultManager(result) &&
			ResultManager(result).neverResumeResult
		) {
			isOngoingGame = true;
		}
		if (!isOngoingGame) {
			newState = setIn(newState, "screenWarning", screenWarning);
		}
	} else {
		newState = setIn(newState, "screenWarning", null);
	}
	return newState;
};

const logVersionStuck = function(storage, frontendVersion, backendVersion) {
	// We use this storage entry to avoid logging E_VERSION_STUCK multiple
	// times.
	const wasStuck = storage.getItem("versionWasStuck");
	if (!wasStuck) {
		ErrorUtil.log(
			"E_VERSION_STUCK",
			"Version checking forced refresh on user, " +
				"but new code was not loaded.",
			{
				frontendVersion,
				backendVersion,
			}
		);
		storage.setItem("versionWasStuck", 1);
	}
};

//
// Exported functions to augment a vault updater
//

const augment = function(updater) {
	updater.addActionHandler(actionHandlers);

	updater.addPostUpdateHook(stopSounds, "core:stopSounds");
	updater.addPostUpdateHook(checkScreen, "core:checkScreen");

	updater.addPreUpdateHook(
		lockAPIOnTooManyRequests,
		"core:lockAPIOnTooManyRequests"
	);

	updater.addPostUpdateHook(validatePage, "core:validatePage", -1000);
	updater.addPostUpdateHook(checkPageChange, "core:checkPageChange", -900);

	updater.addPostUpdateHook(
		OpsPageData.updatePageData,
		"core:updatePageData",
		0
	);

	updater.addPostUpdateHook(
		OpsChapterPath.updateChapterPathData,
		"core:updateChapterPathData"
	);

	updater.addPostUpdateHook(
		TimeTracking.updateTimeTracking,
		"core:updateTimeTracking",
		0
	);

	updater.addPostUpdateHook(
		OpsMarketing.recordEvents,
		"core:recordMarketingEvents",
		100
	);

	updater.addPostUpdateHook(checkCommonAPIError, "core:checkCommonAPIError");

	updater.addPostUpdateHook(
		OpsDemo.demoPostUpdateHook,
		"core:demoPostUpdateHook"
	);

	updater.addPostUpdateHook(checkLevelUp, "core:checkLevelUp");
};

module.exports = {
	augment,
	updateAllSoundSettings,
};
